import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {
  useGraduationYearsHot,
  useMasterGroupedJobTypes,
  useRecruitmentList,
  useRecruitmentTags,
  useRecruitmentTypes,
} from '../../../core/repositories/recruitingInfoRepository';
import {useIndustryCategories} from '../../../core/repositories/reportsRepository';

type Props = {
  contentsLimit: number;
};

export type RecruitmentOrderType =
  | 'recommend'
  | 'recent'
  | 'deadline'
  | 'eventDate';

export const useHandleRecruitingInfoForPc = ({contentsLimit}: Props) => {
  const router = useRouter();
  const {
    page,
    order,
    industryCategoryIDs,
    tagIDs,
    typeIDs,
    groupedJobTypeIDs,
    gy,
  } = router.query;

  const {data: industryCategories, isLoading: isLoadingIndustryCategories} =
    useIndustryCategories({});
  const {data: recruitmentTags, isLoading: isLoadingRecruitmentTags} =
    useRecruitmentTags({});
  const {data: recruitmentTypes, isLoading: isLoadingRecruitmentTypes} =
    useRecruitmentTypes({});
  const {
    data: masterGroupedJobTypes,
    isLoading: isLoadingMasterGroupedJobTypes,
  } = useMasterGroupedJobTypes({});
  const {data: graduationYearsHots, isLoading: isLoadingGraduationYearsHot} =
    useGraduationYearsHot({});

  // 並び順
  const [recruitmentOrder, setRecruitmentOrder] =
    useState<RecruitmentOrderType>(
      (order ?? 'recommend') as RecruitmentOrderType,
    );
  // 業界で絞り込む
  const [filterCategories, setFilterCategories] = useState<number[]>(
    industryCategoryIDs
      ? Array.isArray(industryCategoryIDs)
        ? industryCategoryIDs.map(Number)
        : industryCategoryIDs.split(',').map(Number)
      : [],
  );
  // 対象で絞り込む
  const [filterRecruitmentTags, setFilterRecruitmentTags] = useState<number[]>(
    tagIDs
      ? Array.isArray(tagIDs)
        ? tagIDs.map(Number)
        : tagIDs.split(',').map(Number)
      : [],
  );
  // 種類で絞り込む
  const [filterRecruitmentTypes, setFilterRecruitmentTypes] = useState<
    number[]
  >(
    typeIDs
      ? Array.isArray(typeIDs)
        ? typeIDs.map(Number)
        : typeIDs.split(',').map(Number)
      : [],
  );
  // 職種で絞り込む
  const [filterMasterGroupedJobTypes, setFilterMasterGroupedJobTypes] =
    useState<number[]>(
      groupedJobTypeIDs
        ? Array.isArray(groupedJobTypeIDs)
          ? groupedJobTypeIDs.map(Number)
          : groupedJobTypeIDs.split(',').map(Number)
        : [],
    );
  // 卒年で絞り込む
  const [filterGraduationYear, setFilterGraduationYear] = useState<
    number | undefined
  >(gy ? Number(gy) : undefined);

  // ページネーション
  const [currentPage, setCurrentPage] = useState<number>(Number(page) || 1);
  const [queryParams, setQueryParams] = useState({
    limit: contentsLimit,
    industryCategoryIDs: filterCategories,
    order: recruitmentOrder ?? 'recommend',
    tagIDs: filterRecruitmentTags,
    typeIDs: filterRecruitmentTypes,
    groupedJobTypeIDs: filterMasterGroupedJobTypes,
    gy: filterGraduationYear,
    allgy: filterGraduationYear === undefined ? 'true' : 'false',
    page: currentPage,
  });

  const {data, isLoading} = useRecruitmentList({
    queryParams,
  });

  const totalCount = data?.totalCount || 0;
  const lastPage = Math.ceil(totalCount / contentsLimit) ?? 1;

  useEffect(() => {
    setCurrentPage(1);
    setQueryParams({
      limit: contentsLimit,
      industryCategoryIDs: filterCategories,
      order: recruitmentOrder ?? 'recommend',
      tagIDs: filterRecruitmentTags,
      typeIDs: filterRecruitmentTypes,
      groupedJobTypeIDs: filterMasterGroupedJobTypes,
      gy: filterGraduationYear,
      allgy: filterGraduationYear === 0 ? 'true' : 'false',
      page: 1,
    });
  }, [
    filterCategories,
    recruitmentOrder,
    filterRecruitmentTags,
    filterRecruitmentTypes,
    filterMasterGroupedJobTypes,
    filterGraduationYear,
  ]);

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      page: currentPage,
    });
  }, [currentPage]);

  useEffect(() => {
    const updateRouter = async () => {
      const {limit, ...restQueryParams} = queryParams;
      const currentQuery = router.query;
      // クエリパラメータが変更された場合のみrouter.pushを呼び出す
      if (JSON.stringify(currentQuery) !== JSON.stringify(restQueryParams)) {
        const isScrollReset =
          Number(router.query.page) !== Number(restQueryParams.page);
        await router.push(
          {
            pathname: router.pathname,
            query: {
              ...restQueryParams,
            },
          },
          undefined,
          {scroll: isScrollReset},
        );
      }
    };

    void updateRouter();
  }, [queryParams]);

  return {
    data,
    isLoading,
    industryCategories,
    recruitmentTags,
    recruitmentTypes,
    masterGroupedJobTypes,
    graduationYearsHots,
    filterGraduationYear,
    recruitmentOrder,
    filterCategories,
    filterRecruitmentTags,
    filterRecruitmentTypes,
    filterMasterGroupedJobTypes,
    setRecruitmentOrder,
    setFilterCategories,
    setFilterRecruitmentTags,
    setFilterRecruitmentTypes,
    setFilterMasterGroupedJobTypes,
    setFilterGraduationYear,
    isLoadingIndustryCategories,
    isLoadingRecruitmentTags,
    isLoadingRecruitmentTypes,
    isLoadingMasterGroupedJobTypes,
    isLoadingGraduationYearsHot,
    currentPage,
    setCurrentPage,
    lastPage,
  };
};
